import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { rem } from '@triumph/design-system';
import { Accordion } from 'primereact/accordion';

import { CONTENT_PADDING_TOP } from '@/components/shared/components/Layout/Layout.StyledComponents';
import { Pill, PillButton } from '@/components/shared/components/Pill';
import { FOCUS_RING_SHADOW } from '@/components/shared/constants/focusRing';

export const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${rem(24)};
    padding: 16px 0;
`;

// Set on a section by the component; subsection headers stick just below the section header.
export const SECTION_HEADER_HEIGHT_VAR = '--conditions-section-header-height';
export const SUBSECTION_HEADER_HEIGHT_VAR = '--conditions-subsection-header-height';

// Section and header colors follow the DDD card (DDDCard / DDDCardHeader / DDDCardSectionTitle).
export const SectionWrapper = styled.section`
    border-radius: ${rem(10)};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    /* clip, not hidden: hidden makes the section a scroll container, which stops sticky headers. */
    overflow: clip;
    background: #ffffff;
`;

// Row action buttons sit at z-index 1; section headers stack above subsection headers.
export const SectionHeader = styled.div<{ $collapsed?: boolean; $subsection?: boolean }>`
    position: sticky;
    top: ${({ $subsection }) =>
        $subsection
            ? `calc(var(${SECTION_HEADER_HEIGHT_VAR}, 0px) - ${CONTENT_PADDING_TOP})`
            : `calc(-1 * ${CONTENT_PADDING_TOP})`};
    z-index: ${({ $subsection }) => ($subsection ? 2 : 3)};
    display: flex;
    align-items: center;
    gap: 32px;
    padding: ${({ $subsection }) => rem($subsection ? 10 : 12)} ${rem(20)};
    background: ${({ $subsection }) => ($subsection ? '#f8fafc' : '#f1f5f9')};
    border-bottom: ${({ $collapsed }) => ($collapsed ? 'none' : '1px solid #e2e8f0')};
`;

export const Subsection = styled.div`
    & + & {
        border-top: 1px solid #e2e8f0;
    }
`;

export const SectionToggle = styled.button`
    display: flex;
    flex: 1;
    align-items: center;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
`;

// Open's header also holds action buttons, so its toggle hugs the title instead of filling the row.
export const OpenSectionToggle = styled(SectionToggle)`
    flex: 0 1 auto;
`;

export const SectionChevron = styled.span`
    width: ${rem(12)};
`;

export const SectionTitle = styled.div<{ $subsection?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: ${({ $subsection }) => ($subsection ? rem(15) : rem(17))};
    font-weight: 600;
    color: #334155;
`;

export const SectionActions = styled.div`
    display: flex;
    gap: 8px;
`;

export const SectionDivider = styled.div`
    width: 1px;
    align-self: stretch;
    background: #e2e8f0;
`;

export const ConditionsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 20px;
    background: #f8fafc;
`;

export const ConditionAnchor = styled.div<{ isFocused?: boolean }>`
    border-radius: 6px;
    transition: box-shadow 200ms ease-in-out;
    box-shadow: ${({ isFocused }) => (isFocused ? FOCUS_RING_SHADOW : 'none')};
`;

export const StyledAccordion = styled(Accordion)<{ isDeleted?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 16px;
    background: #f9fafb;

    .p-accordion-tab {
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        background: ${({ isDeleted }) => (isDeleted ? '#fafafa' : '#ffffff')};
        box-shadow:
            0 1px 2px rgba(17, 24, 39, 0.04),
            0 1px 3px rgba(17, 24, 39, 0.06);
        overflow: hidden;
        transition:
            border-color 120ms ease-in-out,
            box-shadow 120ms ease-in-out;
        opacity: ${({ isDeleted }) => (isDeleted ? 0.6 : 1)};
    }
    .p-accordion-tab:hover {
        border-color: ${({ theme }) => `${theme.colors.formControl.border}66`};
        box-shadow:
            0 2px 4px rgba(17, 24, 39, 0.06),
            0 2px 6px rgba(17, 24, 39, 0.08);
    }
    .p-accordion-tab:focus-within {
        border-color: ${({ theme }) => theme.colors.formControl.border};
        box-shadow:
            0 2px 6px rgba(17, 24, 39, 0.08),
            0 2px 8px rgba(17, 24, 39, 0.1);
    }
    .p-accordion-header-link {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px 16px;
        background: ${({ isDeleted }) => (isDeleted ? '#fafafa' : '#ffffff')};
        border: none;
        cursor: pointer;
    }
    .p-accordion-header-link > .p-accordion-toggle-icon {
        align-self: flex-start;
        margin-top: 6px;
        flex-shrink: 0;
    }
    .p-accordion-header-link:hover {
        background: ${({ isDeleted }) => (isDeleted ? '#f5f5f5' : '#f9fafb')};
    }
    .p-accordion-header-link:focus {
        outline: none;
        box-shadow: none;
    }
    .p-accordion-content {
        padding: 16px 16px 20px 46px;
        background: ${({ isDeleted }) => (isDeleted ? '#fafafa' : '#ffffff')};
        border: none;
        border-top: 1px dashed #e5e7eb;
    }
`;

export const HeaderRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    flex: 1;
    min-width: 0;
`;

export const Checkbox = styled.div<{ disabled?: boolean }>`
    width: 18px;
    height: 18px;
    border: 1.5px solid #d1d5db;
    border-radius: 4px;
    flex-shrink: 0;
    margin-top: 2px;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
`;

export const HeaderContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-width: 0;
`;

export const Description = styled.div<{ $markdown?: boolean }>`
    font-weight: 600;
    color: #111827;
    font-size: 14px;
    line-height: 22px;
    white-space: ${(props) => (props.$markdown ? 'normal' : 'pre-line')};
    border-left: 4px solid #d1d5db;
    padding-left: 10px;

    ${(props) => (props.$markdown ? 'p { margin: 0; }' : '')}
`;

export const MetaRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    font-size: 12px;
    color: #b8bfc9;
`;

export const NotesHint = styled.div`
    font-size: 12px;
    color: #6b7280;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 4px;
    cursor: pointer;
    transition: background 120ms ease-in-out;

    &:hover {
        background: #f3f4f6;
        color: #374151;
    }
`;

export const NotesDot = styled.span`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #f97316;
    display: inline-block;
`;

export const NotesPreview = styled.span`
    color: #9ca3af;
    font-style: italic;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
`;

export const TimelineWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-bottom: 12px;
`;

export const NoteEntry = styled.div<{
    isFocused?: boolean;
    $continuation?: boolean;
    $joinsNext?: boolean;
}>`
    display: flex;
    gap: 10px;
    align-items: flex-start;
    border-radius: 6px;
    padding: 8px 10px;
    transition: box-shadow 200ms ease-in-out;
    box-shadow: ${({ isFocused }) => (isFocused ? FOCUS_RING_SHADOW : 'none')};
    /* Grouped notes butt up against each other so their text boxes read as one: drop the padding
       at the seam and cancel the timeline gap. */
    ${({ $continuation }) => ($continuation ? 'margin-top: -12px; padding-top: 0;' : '')}
    ${({ $joinsNext }) => ($joinsNext ? 'padding-bottom: 0;' : '')}
`;

/** Sits in the avatar column of a grouped note so each note keeps its own visible time. */
export const ContinuationTime = styled.div`
    width: 32px;
    flex-shrink: 0;
    padding-top: 11px;
    color: #9ca3af;
    font-size: 10px;
    line-height: 1.2;
    text-align: center;
`;

export const Avatar = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #e0e7ff;
    color: #3730a3;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
    overflow: hidden;
`;

export const AvatarImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    background: #ffffff;
`;

export const NoteBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
`;

export const NoteHeader = styled.div`
    font-size: 12px;
    color: #4b5563;
    font-weight: 500;
`;

export const NoteText = styled.div<{ $markdown?: boolean; $joinsPrev?: boolean; $joinsNext?: boolean }>`
    background: #f3f4f6;
    padding: 10px 12px;
    border-radius: 6px;
    /* Square off the corners where a grouped note meets its neighbour. */
    ${({ $joinsPrev }) => ($joinsPrev ? 'border-top-left-radius: 0; border-top-right-radius: 0;' : '')}
    ${({ $joinsNext }) => ($joinsNext ? 'border-bottom-left-radius: 0; border-bottom-right-radius: 0;' : '')}
    color: #111827;
    font-size: 13px;
    line-height: 1.4;
    white-space: ${(props) => (props.$markdown ? 'normal' : 'pre-line')};

    ${(props) => (props.$markdown ? 'p { margin: 0; }' : '')}
`;

export const AutoResolveButton = styled.button`
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
    padding: 4px 10px;
    border-radius: 4px;
    border: 1px solid #86efac;
    background: #f0fdf4;
    color: #15803d;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    &:hover {
        background: #dcfce7;
    }
`;

export const FormWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid #f3f4f6;

    [role='textbox'] {
        font-size: 15px;
    }
`;

export const FormLabel = styled.label`
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
`;

export const Textarea = styled.textarea<{ hasError?: boolean }>`
    width: 100%;
    min-height: 90px;
    padding: 10px 12px;
    border: 1px solid ${({ hasError }) => (hasError ? '#dc2626' : '#d1d5db')};
    border-radius: 6px;
    font-family: inherit;
    font-size: 13px;
    resize: vertical;
    box-sizing: border-box;
    &::placeholder {
        color: #9ca3af;
        opacity: 1;
    }
    &:focus {
        outline: none;
        border-color: ${({ hasError }) => (hasError ? '#dc2626' : '#2563eb')};
        box-shadow: 0 0 0 2px ${({ hasError }) => (hasError ? '#fecaca' : '#bfdbfe')};
    }
`;

export const ErrorText = styled.div`
    color: #dc2626;
    font-size: 12px;
`;

export const FormActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: auto;
`;

export const NoNotesText = styled.div`
    font-size: 12px;
    color: #9ca3af;
    font-style: italic;
`;

export const ModalContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 8px 0;
    min-width: 480px;
    min-height: 50vh;
`;

// Compact container for short prompt dialogs; no min-height so it hugs its content.
export const PromptModalContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 8px 0;
    min-width: 480px;
`;

export const ModalSubtitle = styled.p`
    margin: 0;
    color: #6b7280;
    font-size: 13px;
`;

export const ModalField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const AppliesToMultiSelectWrapper = styled.div`
    width: 100%;
    position: relative;

    .p-multiselect {
        font-size: 14px;

        .p-multiselect-label {
            font-size: 14px;
            padding: 10px 12px;
        }

        .p-multiselect-trigger {
            width: 40px;
        }

        .p-multiselect-token {
            font-size: 13px;
            padding: 4px 8px;
        }

        .p-multiselect-token-label {
            font-size: 13px;
        }

        .p-multiselect-token-icon {
            font-size: 14px;
            width: 14px;
            height: 14px;
            margin-left: 6px;
        }
    }

    .p-multiselect-panel {
        top: 100%;
        left: 0;
        width: 100%;
        min-width: 0;
        transform: none;
    }

    .p-dropdown {
        font-size: 14px;

        .p-dropdown-label {
            font-size: 14px;
            padding: 10px 12px;
        }

        .p-dropdown-clear-icon {
            margin-right: 8px;
            color: #000000;
            background-color: #e2e2e2;
            border-radius: 50%;
            padding: 1px;
            transform: scale(2);
            border: 1px solid rgba(0, 0, 0, 0.25);

            &:focus,
            &:active,
            &:focus-visible {
                outline: none;
                box-shadow: 0 0 0 1px #93c5fd;
            }
        }

        .p-dropdown-trigger {
            margin-left: 12px;
        }
    }

    .p-dropdown-panel .p-dropdown-items .p-dropdown-item {
        display: block;
        width: 100%;
    }
`;

export const ReadonlyConditionTitle = styled.div<{ $markdown?: boolean }>`
    padding: 10px 12px;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    color: #6b7280;
    font-size: 13px;
    font-style: italic;
    white-space: ${(props) => (props.$markdown ? 'normal' : 'pre-wrap')};
    word-wrap: break-word;

    ${(props) => (props.$markdown ? 'p { margin: 0; }' : '')}
`;

export const HelpText = styled.div`
    color: #6b7280;
    font-size: 12px;
    font-style: italic;
`;

export const ResolveButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 40px;
    padding: 0 16px;
    border-radius: 4px;
    border: none;
    background: #16a34a;
    color: #ffffff;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;
    &:hover:not(:disabled) {
        background: #15803d;
    }
    &:disabled {
        background: #2c6943;
        cursor: not-allowed;
    }
`;

export const ProblematicButton = styled(ResolveButton)`
    background: #d97706;
    &:hover:not(:disabled) {
        background: #b45309;
    }
    &:disabled {
        background: #a5773f;
    }
`;

export const ResolvedNoteText = styled.div<{ $markdown?: boolean }>`
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    padding: 10px 12px;
    border-radius: 6px;
    color: #14532d;
    font-size: 13px;
    line-height: 1.4;
    white-space: ${(props) => (props.$markdown ? 'normal' : 'pre-line')};

    ${(props) => (props.$markdown ? 'p { margin: 0; }' : '')}
`;

export const ResolvedBadge = styled.span`
    color: #16a34a;
    font-size: 12px;
    font-weight: 600;
    margin-left: 6px;
`;

export const StrikethroughDescription = styled.div<{ $markdown?: boolean }>`
    font-weight: 600;
    color: #9ca3af;
    font-size: 14px;
    line-height: 22px;
    text-decoration: line-through;
    white-space: ${(props) => (props.$markdown ? 'normal' : 'pre-line')};
    border-left: 4px solid #d1d5db;
    padding-left: 10px;

    ${(props) => (props.$markdown ? 'p { margin: 0; }' : '')}
`;

export const FilledCheckbox = styled.div`
    width: 18px;
    height: 18px;
    border-radius: 4px;
    background: #16a34a;
    flex-shrink: 0;
    margin-top: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-size: 11px;
    font-weight: 700;
`;

export const RequiredAsterisk = styled.span`
    color: #dc2626;
    margin-left: 4px;
`;

export const SelectAllLink = styled.button`
    background: none;
    border: none;
    padding: 0;
    color: #4f46e5;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    align-self: flex-start;
    &:hover {
        text-decoration: underline;
    }
    &:disabled {
        color: #9ca3af;
        cursor: not-allowed;
        text-decoration: none;
    }
`;

export const ConditionPickerList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 240px;
    overflow-y: auto;
`;

export const ConditionPickerRow = styled.label<{ selected?: boolean }>`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid ${({ selected }) => (selected ? '#c7d2fe' : '#e5e7eb')};
    background: ${({ selected }) => (selected ? '#eef2ff' : '#ffffff')};
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    color: #111827;
    &:hover {
        border-color: #c7d2fe;
    }
`;

export const ConditionPickerLabel = styled.span<{ $markdown?: boolean }>`
    flex: 1;
    font-weight: 500;
    white-space: ${(props) => (props.$markdown ? 'normal' : 'pre-wrap')};
    word-wrap: break-word;
    min-width: 0;
    padding-top: 3px;

    ${(props) => (props.$markdown ? 'p { margin: 0; }' : '')}
`;

export const ContentFooter = styled.div`
    display: flex;
    justify-content: flex-end;
`;

export const AttachedFileList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 320px;
    overflow-y: auto;
`;

export const AttachedFileRow = styled.button`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    text-align: left;
    cursor: pointer;
    font-family: inherit;
    width: 100%;
    transition:
        border-color 120ms ease-in-out,
        background 120ms ease-in-out;
    &:hover {
        border-color: #c7d2fe;
        background: #f5f7ff;
    }
`;

export const AttachedFileIcon = styled.div`
    font-size: 22px;
    flex-shrink: 0;
`;

export const AttachedFileBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
`;

export const AttachedFileName = styled.div`
    font-weight: 600;
    color: #111827;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const AttachedFileMeta = styled.div`
    color: #6b7280;
    font-size: 12px;
`;

export const AttachedFileTypeChip = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    background: #e1f0f9;
    color: #2679ad;
    border: 1px solid #b8d8ea;
    flex-shrink: 0;
`;

export const TemplateOption = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;

    & > span:first-of-type {
        flex: 1;
        min-width: 0;
    }
`;

const watcherAvatarStyles = css`
    .p-avatar {
        width: 4.5rem;
        height: 4.5rem;
        border: 2px solid #ffffff;
        background: #e0e7ff;
        color: #3730a3;
        font-size: 14px;
        font-weight: 700;

        .p-avatar-icon {
            font-size: 2rem;
        }
    }
`;

export const WatcherStackButton = styled.button`
    position: relative;
    display: inline-flex;
    align-items: center;
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    border-radius: 50%;

    ${watcherAvatarStyles}

    &:focus-visible {
        outline: 2px solid #93c5fd;
        outline-offset: 2px;
    }

    &[aria-busy='true'] .p-avatar,
    &[aria-busy='true'] .p-avatar-group {
        opacity: 0.5;
    }
`;

export const WatcherStackLoadingOverlay = styled.span`
    position: absolute;
    inset: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
`;

export const WatcherRowAvatarWrapper = styled.span`
    display: inline-flex;
    flex-shrink: 0;

    ${watcherAvatarStyles}
`;

export const WatcherPopoverBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 260px;
    max-width: 320px;
`;

export const WatcherPopoverTitle = styled.div`
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #4b5563;
`;

export const WatcherList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 240px;
    overflow-y: auto;
`;

export const WatcherRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

export const WatcherRowMeta = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
`;

export const WatcherRowName = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const WatcherRowDate = styled.span`
    font-size: 11px;
    color: #9ca3af;
`;

/** Reserved for a future per-watcher remove control -- no backend endpoint exists yet. */
export const WatcherRowAction = styled.span`
    flex-shrink: 0;
    width: 20px;
`;

export const NoWatchersText = styled.div`
    font-size: 12px;
    color: #9ca3af;
    font-style: italic;
`;

export const WatchThisButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 12px;
    border-radius: 4px;
    border: 1px solid #c7d2fe;
    background: #eef2ff;
    color: #3730a3;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    &:hover:not(:disabled) {
        background: #e0e7ff;
    }
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

export const ConditionActionButton = styled.button`
    position: absolute;
    top: 6px;
    left: 14px;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 6px;
    border: none;
    background: transparent;
    color: #6b7280;
    cursor: pointer;
    padding: 0;
    flex-shrink: 0;

    & .pi {
        font-size: 20px;
    }

    &:hover {
        background: #e5e7eb;
        color: #111827;
    }
    &:focus-visible {
        outline: 2px solid #93c5fd;
        outline-offset: 2px;
    }
`;

export const ActionMenuBody = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 200px;
    padding: 4px 0;
`;

export const ActionMenuText = styled.div`
    padding: 10px 14px;
    max-width: 320px;
    font-size: 14px;
    color: #111827;
    word-break: break-all;
    user-select: text;
`;

export const ActionMenuItem = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    background: transparent;
    border: none;
    text-align: left;
    font-family: inherit;
    font-size: 14px;
    color: #111827;
    cursor: pointer;

    & .pi {
        font-size: 14px;
    }

    &:hover:not(:disabled):not([aria-disabled='true']) {
        background: #f3f4f6;
    }
    &:disabled,
    &[aria-disabled='true'] {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

export const ActionMenuItemDanger = styled(ActionMenuItem)`
    color: #b91c1c;
`;

export const ProductPill = Pill;
export const DocumentsCountPill = PillButton;
export const ResolvedPill = styled(Pill.withComponent('div'))`
    max-width: 100%;
    overflow: hidden;
`;
export const DeletedPill = Pill.withComponent('div');
export const WarningPill = Pill.withComponent('div');
export const ProblematicPill = styled(Pill.withComponent('div'))`
    max-width: 100%;
    overflow: hidden;
`;
export const ProblematicPillButton = styled(PillButton)`
    max-width: 100%;
    overflow: hidden;
    text-align: left;
`;
export const LinkedConditionPill = Pill.withComponent('div');
export const LinkedConditionPillButton = PillButton;
export const DddPendingPill = Pill.withComponent('div');
export const DddPendingPillButton = PillButton;
